import React, { memo } from 'react';
import { LoadingIndicator } from '../ui';
import './CharacterList.css';

/**
 * CharacterList component
 * Displays a list of characters with selection capability
 *
 * @param {Object} props - Component props
 * @param {Array} props.characters - List of character objects
 * @param {Array} props.selectedCharacterIds - IDs of selected characters
 * @param {Function} props.onSelectionChange - Callback when selection changes
 * @param {Function} props.onEditCharacter - Callback when edit is clicked
 * @param {boolean} props.loading - Whether characters are loading
 */
const CharacterList = memo(({
  characters = [],
  selectedCharacterIds = [],
  onSelectionChange,
  onEditCharacter,
  loading = false
}) => {
  // Handler for character selection
  const handleToggleSelection = (character) => {
    const isSelected = selectedCharacterIds.includes(character.id);
    if (onSelectionChange) {
      onSelectionChange(character, !isSelected);
    }
  };

  // Handler for edit button click
  const handleEditClick = (e, character) => {
    e.stopPropagation(); // Prevent selection toggle
    if (onEditCharacter) {
      onEditCharacter(character);
    }
  };

  if (loading) {
    return (
      <div className="character-list character-list--loading">
        <LoadingIndicator size="medium" label="Loading characters..." />
      </div>
    );
  }

  if (characters.length === 0) {
    return (
      <div className="character-list character-list--empty">
        <p>Create one over here! {'->'}</p>
      </div>
    );
  }

  return (
    <div className="character-list">
      {characters.map(character => {
        const isSelected = selectedCharacterIds.includes(character.id);
        
        return (
          <div 
            key={character.id}
            className={`character-list__item ${isSelected ? 'character-list__item--selected' : ''}`}
            onClick={() => handleToggleSelection(character)}
          >
            <div className="character-list__checkbox">
              <span className={`character-list__checkbox-icon ${isSelected ? 'character-list__checkbox-icon--checked' : ''}`}>
                {isSelected && '✓'}
              </span>
            </div>
            
            <div className="character-list__content">
              <div className="character-list__name-container">
                <span className="character-list__name">{character.name.trim()}</span>
                <span className="character-list__creature">, {character.creature}</span>
              </div>
              
              <button 
                className="character-list__edit-button"
                onClick={(e) => handleEditClick(e, character)}
                aria-label={`Edit ${character.name}`}
              >
                Edit
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
});

CharacterList.displayName = 'CharacterList';

export default CharacterList;